import React, { useState, useRef } from 'react';
import {
  Button,
  Tooltip,
  CircularProgress,
  Snackbar,
  LinearProgress
} from '@mui/material';
import { Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SavedSearchSelectorProps } from '../types';
import {
  Check as DefaultIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { ReactComponent as SaveAsIcon } from '../../../images/save_as.svg';
import ExtendedSelect from '../../select';
import SaveDialog from './SaveDialog';
import SaveAsDialog from './SaveAsDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import RenameDialog from './RenameDialog';
import { formatSearchOption } from '../utils/savedSearchHelpers';

const StyledSelect = styled(ExtendedSelect)({
  '& .MuiInputBase-root': {
    background: 'white',
    paddingTop: '1px',
    paddingBottom: '1px'
  }
});

function SavedSearchSelector({
  savedSearches = [],
  currentSearch,
  currentConfig,
  hasUnsavedChanges,
  isLoading,
  error,
  successMessage,
  onSelect,
  onSave,
  onSaveAs,
  onDelete,
  onRename,
  onSetDefault,
  onRemoveDefault,
  onShare,
  disabled = false,
  hideActions = false,
  variant = 'standard',
  className = '',
  style = {},
  theme,
  configMode = 'table',
  isDefaultProfile = false
}: SavedSearchSelectorProps) {
  const isOpaque = configMode === 'opaque';
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const selectRef = useRef(null);

  // Define styling based on variant
  const isCondensed = variant === 'condensed';
  const gap = isCondensed ? '0.25em' : '0.5em';
  const fontSize = isCondensed ? '0.875rem' : undefined; // 14px vs default

  const [isApplying, setIsApplying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSelect = async (value: string | null) => {
    if (onSelect) {
      setIsApplying(true);
      try {
        await onSelect(value || null);
      } finally {
        setIsApplying(false);
      }
    }
  };

  const handleSave = () => {
    if (!currentSearch?.id) {
      // If no current search, show save as dialog
      setShowSaveAsDialog(true);
    } else if (currentSearch.partnerCompanyName) {
      // Can't update partner-provided searches
      setShowSaveAsDialog(true);
    } else {
      setShowSaveDialog(true);
    }
  };

  const handleConfirmSave = async () => {
    setShowSaveDialog(false);
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleSaveAs = async (name: string, shareType: string) => {
    setShowSaveAsDialog(false);
    if (onSaveAs) {
      setIsSaving(true);
      try {
        await onSaveAs(name, shareType);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleDelete = () => {
    if (currentSearch?.id) {
      setShowDeleteDialog(true);
    }
  };

  const handleConfirmDelete = async () => {
    setShowDeleteDialog(false);
    if (onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(currentSearch.id);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleRename = async (newName: string) => {
    setShowRenameDialog(false);
    if (onRename && currentSearch) {
      setIsSaving(true);
      try {
        await onRename(currentSearch.id, newName);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleSetDefault = () => {
    if (currentSearch) {
      if (currentSearch.isDefault && onRemoveDefault) {
        onRemoveDefault(currentSearch.id);
      } else if (onSetDefault) {
        onSetDefault(currentSearch.id);
      }
    }
  };

  // Format options for select
  const selectOptions = savedSearches.map(formatSearchOption);

  const hasFilters = React.useMemo(() => {
    if (isOpaque) {
      return !!currentConfig && Object.keys(currentConfig).length > 0;
    }
    if (!currentConfig) return false;
    const config = currentConfig as any;
    if (!config.filters || !config.filters.filters) return false;
    return config.filters.filters.some((f: any) => !f.system);
  }, [currentConfig, isOpaque]);

  const isPartnerSearch = currentSearch?.partnerCompanyName;
  const isSystemProfile = (currentSearch as any)?.isSystemProfile === true;

  const canSave = isOpaque
    ? hasUnsavedChanges && !isPartnerSearch && !isSystemProfile
    : hasUnsavedChanges && !isPartnerSearch && hasFilters;

  const canSaveAs = isOpaque
    ? !!currentConfig && Object.keys(currentConfig).length > 0
    : true;

  const canDelete = currentSearch?.id && !isPartnerSearch && !isSystemProfile && !isDefaultProfile;
  const canRename = currentSearch?.id && !isPartnerSearch && !isSystemProfile && !isDefaultProfile;
  const canToggleDefault = currentSearch && currentSearch.id;  // Can toggle if there's a current search
  const isCurrentDefault = currentSearch?.isDefault;

  return (
    <>
      {/* Progress bar for loading operations */}
      {(isApplying || isSaving || isDeleting) && (
        <LinearProgress
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000
          }}
        />
      )}

      <div
        className={`saved-search-selector ${className}`}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: gap,
          alignItems: 'center',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          fontSize: fontSize,
          position: 'relative',
          opacity: (isApplying || isSaving || isDeleting) ? 0.7 : 1,
          transition: 'opacity 0.2s',
          ...style
        }}
      >
      <StyledSelect
        ref={selectRef}
        variant="outlined"
        style={{ flex: '1 1 auto', minWidth: '150px', fontSize: fontSize }}
        placeholder="Select saved search..."
        value={currentSearch?.id ? String(currentSearch.id) : ''}
        onChange={handleSelect}
        options={selectOptions}
        disabled={disabled || isLoading || isApplying || isSaving || isDeleting}
        isLoading={isLoading && savedSearches.length === 0}
        isClearable
        controlled={true}
        key={`search-selector-${currentSearch?.id || 'none'}-${savedSearches.length}`}
        {...({} as any)}
      />

      {!hideActions && (
        <>
          <Tooltip title={
            !canToggleDefault ? "Select a saved search first" :
            isCurrentDefault ? "Remove as default" :
            "Set as default"
          }>
            <span>
              <Button
                onClick={handleSetDefault}
                disabled={disabled || isLoading || !canToggleDefault || isApplying || isSaving || isDeleting}
                startIcon={isCurrentDefault ? <ClearIcon /> : <DefaultIcon />}
              >
                {isCurrentDefault ? "Remove Default" : "Set as Default"}
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={canSave ? "Save changes" : "No changes to save"}>
            <span>
              <Button
                color="primary"
                onClick={handleSave}
                disabled={disabled || isLoading || !canSave || isApplying || isSaving || isDeleting}
                startIcon={<SaveIcon style={{ width: '16px', height: '16px' }} />}
              >
                Save
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={canSaveAs ? "Save as new search" : "No filters to save"}>
            <span>
              <Button
                color="primary"
                onClick={() => setShowSaveAsDialog(true)}
                disabled={disabled || isLoading || !canSaveAs || isApplying || isSaving || isDeleting}
                startIcon={<SaveAsIcon style={{ width: '16px', height: '16px' }} />}
              >
                Save As
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={canRename ? "Rename saved search" : "Select a saved search to rename"}>
            <span>
              <Button
                onClick={() => setShowRenameDialog(true)}
                disabled={disabled || isLoading || !canRename || isApplying || isSaving || isDeleting}
                startIcon={<EditIcon />}
              >
                Rename
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={canDelete ? "Delete saved search" : "Cannot delete this search"}>
            <span>
              <Button
                color={canDelete ? "error" : "inherit"}
                onClick={handleDelete}
                disabled={disabled || isLoading || !canDelete || isApplying || isSaving || isDeleting}
                startIcon={<DeleteIcon />}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        </>
      )}

      {isLoading && savedSearches.length > 0 && (
        <Tooltip title="Refreshing saved searches...">
          <CircularProgress size={20} />
        </Tooltip>
      )}

      {/* Dialogs */}
      <SaveDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onConfirm={handleConfirmSave}
        savedSearch={currentSearch}
      />

      <SaveAsDialog
        open={showSaveAsDialog}
        onClose={() => setShowSaveAsDialog(false)}
        onSave={handleSaveAs}
        existingNames={savedSearches.map(s => s.name)}
        allowShare={!currentSearch?.partnerCompanyName}
      />

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        savedSearch={currentSearch}
      />

      <RenameDialog
        open={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
        onConfirm={handleRename}
        savedSearch={currentSearch}
        existingNames={savedSearches.filter(s => s.id !== currentSearch?.id)}
      />

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error">
          {error}
        </Alert>
      </Snackbar>
      </div>

    </>
  );
}

export default SavedSearchSelector;
